/** Kassenbuch und Tagesabschluss (Screens kasse/Kassenbuch, kasse/Tagesabschluss – im MVP in der Verwaltung). */
import { useState } from "react";
import { Link } from "react-router";
import { Button, Dialog, Notice, Seitenzustand, StatusBadge, TextField, Total } from "../ui";
import { ItemRow } from "../ui/ItemRow";
import { Card, Dl, Grid, KpiTile, PageHeader, Select, Split } from "../ui/Layout";
import { ApiFehler } from "../api/client";
import { formatEuro, parseEuro } from "@shared/geld";
import { formatTag, formatUhrzeit, tagVon } from "@shared/zeit";
import { tagPlus } from "@shared/perioden";
import { exportUrl, useKassenbuch, useKassenbuchEintrag, useTagesabschluss, useTagesabschlussSpeichern } from "./geld-api";

const ART: Record<string, string> = { einlage: "Einlage", entnahme: "Entnahme", bareinnahme: "Bareinnahme", auszahlung: "Auszahlung", korrektur: "Korrektur" };

export function Kassenbuch() {
  const [tag, setTag] = useState(tagVon());
  const kb = useKassenbuch(tag, tag);
  const ta = useTagesabschluss(tag);
  const eintrag = useKassenbuchEintrag();
  const abschliessen = useTagesabschlussSpeichern();
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ art: "einlage" as "einlage" | "entnahme" | "korrektur", betrag: "", beschreibung: "" });
  const [barIst, setBarIst] = useState("");
  const [meldung, setMeldung] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const heute = tagVon();
  const istCent = parseEuro(barIst);

  return (
    <>
      <PageHeader
        kicker="Geld"
        title="Kassenbuch und Tagesabschluss"
        sub={tag === heute ? "Heute" : formatTag(tag)}
        actions={
          <>
            <Button variant="quiet" icon="chevron-left" aria-label="Vortag" onClick={() => setTag(tagPlus(tag, -1))} />
            <Button variant="secondary" onClick={() => setTag(heute)}>Heute</Button>
            <Button variant="quiet" icon="chevron-right" aria-label="Folgetag" disabled={tag >= heute} onClick={() => setTag(tagPlus(tag, 1))} />
            <a href={exportUrl("kassenbuch", `${tag.slice(0, 7)}-01`, tag)} className="rg-btn rg-btn--secondary">Export Monat (CSV)</a>
            <Button variant="primary" icon="plus" onClick={() => { setForm({ art: "einlage", betrag: "", beschreibung: "" }); setDialog(true); }}>Eintrag</Button>
          </>
        }
      />
      {meldung ? <Notice tone={meldung.tone}>{meldung.text}</Notice> : null}
      <Split
        main={
          <>
            <Seitenzustand query={ta} lines={6}>
              {(t) => (
                <>
                  <Card title="Umsatz abgleichen" actions={t.abschluss ? <StatusBadge status="paid">Abgeschlossen</StatusBadge> : t.differenzCent === 0 && t.offeneWarenkoerbe.length === 0 ? <StatusBadge status="paid">Stimmt überein</StatusBadge> : <StatusBadge status="attention">Differenz prüfen</StatusBadge>}>
                    <Grid>
                      <KpiTile label="SumUp" value={formatEuro(t.sumup.summeCent)} note={`${t.sumup.anzahl} Zahlungen · Karte ${formatEuro(t.sumup.karteCent)} · Bar ${formatEuro(t.sumup.barCent)}`} />
                      <KpiTile label="Software" value={formatEuro(t.software.summeCent)} note={`${t.software.anzahl} Warenkörbe`} />
                      <KpiTile label="Differenz" value={formatEuro(t.differenzCent)} note={t.zahlungenOhneWarenkorb.length ? `${t.zahlungenOhneWarenkorb.length} Zahlung(en) ohne Warenkorb` : "keine offenen Zahlungen"} />
                    </Grid>
                  </Card>
                  {t.offeneWarenkoerbe.length || t.zahlungenOhneWarenkorb.length ? (
                    <Card title="Offene Fälle" flush>
                      {t.zahlungenOhneWarenkorb.map((z) => <ItemRow key={z.transactionCode} photo={false} title={`Zahlung ohne Zuordnung · ${formatEuro(z.betragCent)}`} meta={`SumUp ${z.zahlungsart === "bar" ? "Bar" : "Karte"} · ${formatUhrzeit(z.zeitpunkt)} · ${z.transactionCode}`} action={<Link to="/laden/verkaeufe?tab=offen" className="rg-btn rg-btn--secondary">Zuordnen</Link>} />)}
                      {t.offeneWarenkoerbe.map((w) => <ItemRow key={w.id} photo={false} title={`Offener Warenkorb #${w.nummer} · ${formatEuro(w.summeCent)}`} meta={`${w.status} · seit ${formatUhrzeit(w.erstelltAt)}`} action={<Link to="/laden/verkaeufe" className="rg-btn rg-btn--quiet">Öffnen</Link>} />)}
                    </Card>
                  ) : null}
                  <Card title="Bargeld-Kassenbuch">
                    <Dl rows={[["Anfangsbestand", formatEuro(t.kasse.anfangCent)], ["Bareinnahmen", formatEuro(t.kasse.bareinnahmenCent)], ["Einlagen / Korrekturen", formatEuro(t.kasse.einlagenCent)], ["Barauszahlungen an Mieter", formatEuro(t.kasse.auszahlungenCent)], ["Entnahmen", formatEuro(t.kasse.entnahmenCent)], ["Soll-Bestand", <span key="s" className="sc-strong">{formatEuro(t.kasse.sollCent)}</span>]]} />
                    {t.abschluss ? (
                      <Notice tone="success" title={`Tag abgeschlossen · ${formatUhrzeit(t.abschluss.abgeschlossenAt)} Uhr`}>Gezählt {formatEuro(t.abschluss.barIstCent)} · Soll {formatEuro(t.abschluss.barSollCent)}{t.abschluss.barIstCent !== t.abschluss.barSollCent ? ` · Differenz ${formatEuro(t.abschluss.barIstCent - t.abschluss.barSollCent)} als Korrektur gebucht` : " · stimmt überein"}.</Notice>
                    ) : (
                      <div className="sc-form" style={{ marginTop: "var(--space-3)", alignItems: "end" }}>
                        <TextField label="Gezählter Bestand" value={barIst} onChange={(e) => setBarIst(e.target.value)} inputMode="decimal" suffix="€" placeholder={(t.kasse.sollCent / 100).toFixed(2).replace(".", ",")} hint={istCent !== null ? (istCent === t.kasse.sollCent ? "Stimmt überein" : `Differenz ${formatEuro(istCent - t.kasse.sollCent)} wird als Korrektur gebucht`) : undefined} />
                        <Button variant="primary" icon="lock" disabled={istCent === null || tag > heute} loading={abschliessen.isPending} onClick={() => confirm(`Tag ${formatTag(tag)} abschließen?`) && abschliessen.mutate({ tag, barIstCent: istCent! }, { onSuccess: () => setMeldung({ tone: "success", text: "Tag abgeschlossen." }), onError: (e) => setMeldung({ tone: "error", text: e instanceof ApiFehler ? e.message : "Abschluss hat nicht geklappt." }) })}>
                          Tag abschließen
                        </Button>
                      </div>
                    )}
                  </Card>
                </>
              )}
            </Seitenzustand>
          </>
        }
        aside={
          <Seitenzustand query={kb} lines={5}>
            {(k) => (
              <Card title="Einträge" flush>
                <div style={{ padding: "0 var(--space-4)" }}><Total amountCent={k.endeCent} label="Bestand" size="lg" caption="nach allen Einträgen des Tages" /></div>
                <table className="rg-table">
                  <thead><tr><th>Zeit</th><th>Art</th><th className="is-num">Betrag</th><th className="is-num">Bestand</th></tr></thead>
                  <tbody>
                    {k.eintraege.length === 0 ? <tr><td colSpan={4} className="rg-table__empty">Keine Einträge an diesem Tag.</td></tr> : null}
                    {k.eintraege.map((e) => <tr key={e.id}><td>{formatUhrzeit(e.erstelltAt)}</td><td>{ART[e.art] ?? e.art}<div className="sc-muted sc-small">{e.beschreibung}{e.mitarbeiterName ? ` · ${e.mitarbeiterName}` : ""}</div></td><td className="is-num">{formatEuro(e.betragCent)}</td><td className="is-num">{formatEuro(e.bestandCent)}</td></tr>)}
                  </tbody>
                </table>
              </Card>
            )}
          </Seitenzustand>
        }
      />
      <Dialog open={dialog} title="Kassenbuch-Eintrag" size="sm" onClose={() => setDialog(false)} actions={<><Button variant="quiet" onClick={() => setDialog(false)}>Abbrechen</Button><Button variant="primary" loading={eintrag.isPending} disabled={!parseEuro(form.betrag) || !form.beschreibung.trim()} onClick={() => eintrag.mutate({ art: form.art, betragCent: parseEuro(form.betrag)!, beschreibung: form.beschreibung.trim() }, { onSuccess: () => setDialog(false), onError: (e) => setMeldung({ tone: "error", text: e instanceof ApiFehler ? e.message : "Das hat nicht geklappt." }) })}>Buchen</Button></>}>
        <div className="app-form">
          <Select label="Art" value={form.art} onChange={(e) => setForm({ ...form, art: e.target.value as typeof form.art })}>
            <option value="einlage">Einlage (z. B. Wechselgeld)</option>
            <option value="entnahme">Entnahme (z. B. Briefmarken)</option>
            <option value="korrektur">Korrektur (± Betrag)</option>
          </Select>
          <TextField label="Betrag" value={form.betrag} onChange={(e) => setForm({ ...form, betrag: e.target.value })} inputMode="decimal" suffix="€" autoFocus />
          <TextField label="Beschreibung" value={form.beschreibung} onChange={(e) => setForm({ ...form, beschreibung: e.target.value })} maxLength={120} />
        </div>
      </Dialog>
    </>
  );
}
